'use strict';

class ChatSupport {
	static CONVERSATION_TTL = 30 * 60 * 1000; // 30 minutes in ms
	static STORAGE_KEY = 'chat_support_session';
	static ENDPOINT = '/chat_support_process';

	static MAX_TEXTAREA_HEIGHT = 160;
	static MIN_TEXTAREA_HEIGHT = 43;

	static POLL_INTERVAL_SEARCHING = 15000;
	static POLL_INTERVAL_ACTIVE = 5000;
	static MIN_SEARCHING_DISPLAY_MS = 1500;

	static KEEPALIVE_INTERVAL_MS = 75000;
	static KEEPALIVE_STORAGE_KEY = 'dev.chat.keepalive_enabled';

	static ALLOWED_UPLOAD_TYPES = ['image/png', 'image/jpeg', 'image/gif'];

	/**
	 * Initializes chat widget options, state flags, elements, and event listeners.
	 * @param {Object} _options Configuration options and localized strings.
	 */
	constructor(_options) {
		this.widget = document.getElementById('chat-support');
		if (!this.widget) return;

		// Text translations
		this.text_escalation_trough_email      = _options.text_escalation_trough_email || 'TEXT_ESCALATION_TROUGH_EMAIL';
		this.text_chat_timeout_warning         = _options.text_chat_timeout_warning || 'TEXT_CHAT_TIMEOUT_WARNING';
		this.text_live_agent_assigned          = _options.text_live_agent_assigned || 'TEXT_LIVE_AGENT_ASSIGNED';
		this.text_confirm_end_conversation      = _options.text_confirm_end_conversation || 'TEXT_CONFIRM_END_CONVERSATION';
		this.text_transcript_send_error        = _options.text_transcript_send_error || 'TEXT_TRANSCRIPT_SEND_ERROR';
		this.text_thank_you_for_chatting       = _options.text_thank_you_for_chatting || 'TEXT_THANK_YOU_FOR_CHATTING';
		this.text_button_start_conversation    = _options.text_button_start_conversation || 'TEXT_BUTTON_START_CONVERSATION';
		this.text_button_stop_conversation     = _options.text_button_stop_conversation || 'TEXT_BUTTON_STOP_CONVERSATION';
		this.text_queue_position               = _options.text_queue_position || 'TEXT_QUEUE_POSITION';
		this.text_conversation_ended_chatbot   = _options.text_conversation_ended_chatbot || 'TEXT_CONVERSATION_ENDED_CHATBOT';
		this.text_conversation_ended_agent     = _options.text_conversation_ended_agent || 'TEXT_CONVERSATION_ENDED_AGENT';
		this.text_chat_support_msg_all_busy    = _options.text_chat_support_msg_all_busy || 'TEXT_CHAT_SUPPORT_MSG_ALL_BUSY';
		this.text_send_transcript_yes_button   = _options.text_send_transcript_yes_button || 'TEXT_SEND_TRANSCRIPT_YES_BUTTON';
		this.text_send_transcript_no_button    = _options.text_send_transcript_no_button  || 'TEXT_SEND_TRANSCRIPT_NO_BUTTON';
		this.text_confirm_conversation_ending   = _options.text_confirm_conversation_ending  || 'TEXT_CONFIRM_CONVERSATION_ENDING';
		this.text_cancel_conversation_ending   = _options.text_cancel_conversation_ending || 'TEXT_CANCEL_CONVERSATION_ENDING';
		this.text_button_restart_conversation  = _options.text_button_restart_conversation || 'TEXT_BUTTON_RESTART_CONVERSATION';
		this.text_button_create_support_ticket = _options.text_button_create_support_ticket || 'TEXT_BUTTON_CREATE_SUPPORT_TICKET';
		this.text_skip_escalation_confirmation  = _options.text_skip_escalation_confirmation || 'TEXT_SKIP_ESCALATION_CONFIRMATION';
		this.text_skip_escalation_button_yes   = _options.text_skip_escalation_button_yes || 'TEXT_SKIP_ESCALATION_BUTTON_YES';
		this.text_skip_escalation_button_no    = _options.text_skip_escalation_button_no || 'TEXT_SKIP_ESCALATION_BUTTON_NO';
		this.text_waiting_for_agent            = _options.text_waiting_for_agent || 'TEXT_WAITING_FOR_AGENT';
		this.text_no_waiting_for_agent         = _options.text_no_waiting_for_agent || 'TEXT_NO_WAITING_FOR_AGENT';
		this.text_no_waiting_for_agent_confirmation = _options.text_no_waiting_for_agent_confirmation || 'TEXT_NO_WAITING_FOR_AGENT_CONFIRMATION';
		this.text_port_in_api_call_failure     = _options.text_port_in_api_call_failure || 'PORT_IN_API_CALL_FAILURE';
		this.text_upload_conditions            = _options.text_upload_conditions || 'TEXT_UPLOAD_CONDITIONS';

		this.text_disclaimer                   = _options.text_disclaimer || 'TEXT_DISCLAIMER';
		this.text_intro                        = _options.text_intro || 'TEXT_INTRO';
		this.text_upload_failure               = _options.text_upload_failure || 'TEXT_UPLOAD_FAILURE';
		this.text_add_more_files                = _options.text_add_more_files || 'TEXT_ADD_MORE_FILES';

		this.bot_display_name                  = _options.bot_display_name || 'Tello Ai';

		this.timeout_interval = 0;
		this.countdown_interval = 0;
		this.chat_settings_loaded = false;

		this.chatbot_authorized = false;
		this.chatbot_session = null;
		this.chatbot_authorize_promise = null;
		this.conversation_id = null;
		this.last_chatbot_date = 0;
		this.transcript = [];

		this.menu = this.widget.querySelector('.menu');
		this.trigger = this.widget.querySelector('.trigger');
		this.history = this.widget.querySelector('.history');
		this.textarea = this.widget.querySelector('textarea');
		this.interaction = this.widget.querySelector('.interaction');
		this.stop_conversation_btn = this.widget.querySelector('[data-action="stop-conversation"]');
		this.file_upload_holder = this.widget.querySelector('.upload-container');
		this.attachment_modal = null;
		this._file_upload_is_live = false;
		this._file_upload_server_available = false;

		this.max_upload_files = 0;
		this.max_upload_file_size = 0;
		this.selected_upload_files = [];
		this.upload_buttons_container = this.file_upload_holder?.querySelector('.upload-buttons') ?? null;
		this.upload_more_btn = null;
		this.upload_more_wrapper = null;
		this.upload_picker_input = null;
		this.upload_submit_btn = null;
		this.upload_label_el = null;
		this._upload_submit_in_progress = false;
		// files the user meant to send vs. managed to send since the uploader was last (re-)enabled
		this._upload_cycle = { intended: 0, succeeded: 0 };
		// last raw `upload_enabled` seen from the server, used to detect an agent re-enabling uploads
		this._upload_server_enabled = null;
		// set after we disable uploads, until a poll confirms it (ignores stale in-flight polls)
		this._upload_disable_pending = false;
		this._initUploadPreview();

		this.clear_conversation_btn = this.widget.querySelector('[data-action="clear"]');
		this.initial_message = this.widget.querySelector('.message.initial');

		this.keep_alive_checkbox = document.getElementById('dev_keepalive');
		this.keep_alive_wrapper = this.keep_alive_checkbox?.closest('label');
		this._keepalive_interval = null;
		this._keepalive_enabled = false;

		this.typing_indicator = `<div class="typing-indicator">
			<span></span>
			<span></span>
			<span></span>
		</div>`;

		this.searching_indicator = `<div class="searching-indicator message bot x-body-md">
			<div class="message-row">
				<div class="avatar">
					<img src="${url(`/images/tello/druid/bot-profile.png`)}" alt="" />
				</div>
				<div class="bubble">
					<div>${this.text_waiting_for_agent}</div>
					<div class="searching-status">
						<span class="searching-spinner"></span>
						<span class="queue-position"></span>
					</div>
					<div>
						${this.text_no_waiting_for_agent}
					</div>
					<div class="quick-replies">
						<button type="button" class="btn" data-action="no-waiting-for-agent" value="Do not wait for an agent">${this.text_no_waiting_for_agent_confirmation}</button>
					</div>
				</div>
			</div>
		</div>`;

		this.loading_indicator = `<div class="chat-loading-indicator">
			<span class="chat-loading-spinner"></span>
		</div>`;

		this.last_update = 0;
		this.poll_timer = null;
		this.polling_stopped = false;
		this.polling_in_flight = false;
		this.chatbot_authorized = false;
		this.pending_sent_count = 0;
		this.pending_upload_count = 0;
		this.agent_joined = false;
		this.agent_notice_shown = false;
		this.escalated = false;
		this.awaiting_quick_reply = false;
		this.awaiting_clear_confirmation = false;
		this.awaiting_agent_connection = false;
		this.greeting_in_progress = false;

		// static texts are injected only when the widget is opened so crawlers/bots don't index them
		this._static_texts_rendered = false;

		this.trigger.addEventListener('click', e => {
			e.preventDefault();

			this._renderStaticTexts();

			this.widget.classList.toggle('open');
			document.body.classList.toggle('chat-support-open');

			// return false; // ! REMOVE THIS!!!

			if (this.widget.classList.contains('open')) {
				if (!this.chat_settings_loaded) {
					this.chat_settings_loaded = true;
					this._loadChatSettings();
				}

				this.markSessionAsOpen();

				const session = this.getStoredSession();
				const is_fresh_conversation = session?.mode !== 'live' && !session?.transcript?.length;

				if (is_fresh_conversation) {
					this._showInitialGreeting();
				} else {
					this.resumeOrStartConversation();
				}
			}

			this._scrollToBottom();
		});

		this.history.addEventListener('click', e => {
			const attachment_link = e.target.closest('.attachment_link');
			if (attachment_link) {
				e.preventDefault();
				this.openAttachmentModal(attachment_link);
				return;
			}

			const skip_btn = e.target.closest('[data-action="no-waiting-for-agent"]');
			if (skip_btn) {
				e.preventDefault();
				this._showSkipEscalationConfirmation(skip_btn);
				return;
			}

			const btn = e.target.closest('.quick-reply');
			if (!btn) return;
			e.preventDefault();

			btn.closest('.quick-replies')?.querySelectorAll('button').forEach(b => b.disabled = true);

			const { action } = btn.dataset;
			const handlers = {
				'confirm-clear-yes': () => {
					this._appendBubble('user', btn.textContent);
					this._onClearConfirmation(true);
				},

				'confirm-clear-no': () => {
					this._appendBubble('user', btn.textContent);
					this._onClearConfirmation(false);
				},

				'email-transcript-yes': () => this._onEmailTranscriptResponse(true),
				'email-transcript-no': () => this._onEmailTranscriptResponse(false),

				'bot-ended-restart': () => this._startNewConversation(),
				'start-new-conversation': () => this._startNewConversation(),
				'ticket-ended-restart': () => this._startNewConversation(),
				'no-agents-acknowledge': () => this._onNoAgentsAcknowledge(),

				'confirm-skip-escalation-yes': () => this._onSkipEscalationConfirmation(true),
				'confirm-skip-escalation-no': () => this._onSkipEscalationConfirmation(false)
			};

			if (handlers[action]) {
				handlers[action]();
				return;
			}

			this.awaiting_quick_reply = false;
			this.sendMessage(btn.dataset.value, btn.textContent);
		});

		this.menu.querySelectorAll('button').forEach(button => {
			button.addEventListener('click', e => {
				e.preventDefault();

				const action = button.dataset.action;
				if (!action) return;

				switch (action) {
					case 'close': this.closeChat(true); break;
					case 'maximize': this.widget.classList.add('maximized'); break;
					case 'minimize': this.widget.classList.remove('maximized'); break;
					case 'clear': this.handleClearAction(); break;
					default: console.warn(`Unknown chat support action: ${action}`);
				}
			});
		});

		this.stop_conversation_btn?.addEventListener('click', e => {
			e.preventDefault();
			this.handleClearAction();
		});

		this.textarea.addEventListener('input', () => {
			const { value } = this.textarea;
			const needs_expand = value.length > 40 || value.includes('\n');

			if (needs_expand) {
				this.textarea.style.height = 'auto';
				this.textarea.style.height = Math.min(this.textarea.scrollHeight, ChatSupport.MAX_TEXTAREA_HEIGHT) + 'px';
			} else {
				this.textarea.style.height = ChatSupport.MIN_TEXTAREA_HEIGHT + 'px';
			}
		});

		this.textarea.addEventListener('keydown', e => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				this.interaction.querySelector('form').requestSubmit();
			}
		});

		this.interaction.querySelector('form').addEventListener('submit', e => {
			e.preventDefault();

			const message = this.textarea.value.trim();
			if (message) this.sendMessage(message);
		});

		// dev-only keep-alive checkbox: restore last enabled preference, actual start/stop
		// is driven by _setKeepAliveVisible() whenever a live conversation starts/ends
		if (this.keep_alive_checkbox) {
			this._keepalive_enabled = localStorage.getItem(ChatSupport.KEEPALIVE_STORAGE_KEY) === 'true';
			this.keep_alive_checkbox.checked = this._keepalive_enabled;

			this.keep_alive_checkbox.addEventListener('change', e => {
				this._keepalive_enabled = e.target.checked;
				localStorage.setItem(ChatSupport.KEEPALIVE_STORAGE_KEY, String(this._keepalive_enabled));

				if (this._keepalive_enabled) {
					this._startKeepAlive();
				} else {
					this._stopKeepAliveTimer();
				}
			});
		}

		this.autoOpenIfNeeded();
	}

	/**
	 * POSTs an action to the chat support endpoint.
	 * @param {string} action Backend action name.
	 * @param {Object|FormData} [params={}] Request fields; FormData is sent as-is (e.g. file uploads).
	 * @returns {Promise<Object|undefined>} Parsed JSON response.
	 */
	_post(action, params = {}) {
		const body = params instanceof FormData ? params : new URLSearchParams(params);
		body.set('action', action);

		return fetcher(url(ChatSupport.ENDPOINT), { method: 'POST', body }).ready;
	}

	/**
	 * Wires up the multi-image selection preview: picking a file swaps that slot's
	 * `.file-pick` button for a thumbnail, and the shared "add more"/remove controls
	 * are toggled based on how many of the slots are filled. The number of slots follows
	 * the server-reported `uploads_remaining` (see `_setMaxUploadFiles`).
	 */
	_initUploadPreview() {
		if (!this.upload_buttons_container) return;

		this.upload_more_btn = this._ensureUploadMoreButton();
		this.upload_more_wrapper = this.upload_more_btn.closest('.upload-button-wrapper');
		this._ensureUploadWrapperCount(Math.max(this.max_upload_files, 1));

		this.upload_picker_input = document.createElement('input');
		this.upload_picker_input.type = 'file';
		this.upload_picker_input.accept = ChatSupport.ALLOWED_UPLOAD_TYPES.join(',');
		this.upload_picker_input.multiple = true;
		this.upload_picker_input.className = 'upload-picker-input';
		this.upload_picker_input.setAttribute('aria-hidden', 'true');
		this.upload_buttons_container.appendChild(this.upload_picker_input);

		this.upload_picker_input.addEventListener('change', e => {
			const files = Array.from(e.target.files ?? []).filter(f => ChatSupport.ALLOWED_UPLOAD_TYPES.includes(f.type));
			e.target.value = '';
			if (!files.length) return;

			this._addSelectedUploadFiles(files);
		});

		this.upload_buttons_container.addEventListener('click', e => {
			const remove_btn = e.target.closest('.file-remove');
			if (remove_btn) {
				e.preventDefault();
				this._removeSelectedUploadFile(remove_btn.closest('.upload-button-wrapper'));
				return;
			}

			if (e.target.closest('.file-pick') || e.target.closest('.file-more')) {
				e.preventDefault();
				this._openUploadPicker();
			}
		});

		this.upload_submit_btn = this.file_upload_holder.querySelector('.upload-submit');
		this.upload_submit_btn?.addEventListener('click', e => {
			e.preventDefault();
			this._submitSelectedUploads();
		});

		this.upload_label_el = this.file_upload_holder.querySelector('.upload-conditions');
		this._renderUploadConditions();

		this._observeUploadContainerHeight();
		this._refreshUploadControlsVisibility();
	}

	/**
	 * Mirrors the upload container's height into `--cs-upload-container-height` on the widget,
	 * so `.history` can reserve matching bottom padding for the overlaid panel.
	 */
	_observeUploadContainerHeight() {
		if (typeof ResizeObserver === 'undefined') return;

		new ResizeObserver(([entry]) => {
			const height = Math.ceil(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.offsetHeight);
			if (!height) return;

			const at_bottom = this.history.scrollHeight - this.history.scrollTop - this.history.clientHeight < 20;
			this.widget.style.setProperty('--cs-upload-container-height', `${height}px`);

			// keep the latest message visible when the panel grows while the user is at the bottom
			if (at_bottom) this._scrollToBottom();
		}).observe(this.file_upload_holder);
	}

	/**
	 * Writes the chatbot name, disclaimer and intro texts into the widget. Runs once,
	 * the first time the widget is opened.
	 */
	_renderStaticTexts() {
		if (this._static_texts_rendered) return;
		this._static_texts_rendered = true;

		const title = this.widget.querySelector('.discussion .toolbar .title');
		title?.setAttribute('title', this.bot_display_name);

		const title_heading = title?.querySelector('h2');
		if (title_heading) title_heading.textContent = this.bot_display_name;

		const disclaimer = this.history.querySelector('.disclaimer');
		if (disclaimer) disclaimer.innerHTML = this.text_disclaimer;

		const intro = this.history.querySelector('.initial .hello');
		if (intro) intro.innerHTML = this.text_intro;
	}

	/**
	 * Returns every `.upload-button-wrapper` that acts as an image slot, i.e. excluding
	 * the wrapper that holds the shared "Add more" button.
	 * @returns {HTMLElement[]} Pick-slot wrapper elements.
	 */
	_getPickWrappers() {
		return Array.from(this.upload_buttons_container.querySelectorAll('.upload-button-wrapper'))
			.filter(w => w !== this.upload_more_wrapper);
	}

	/**
	 * Ensures there are exactly `count` pick-slot `.upload-button-wrapper` elements,
	 * cloning the first one (icon markup included) to fill in any missing slots and
	 * removing surplus empty ones. The first wrapper is always kept as the clone template.
	 * @param {number} count Desired number of wrapper slots.
	 */
	_ensureUploadWrapperCount(count) {
		const wrappers = this._getPickWrappers();
		const template = wrappers[0];
		if (!template) return;

		for (let i = wrappers.length - 1; i > 0 && wrappers.length > count; i--) {
			if (this.selected_upload_files.some(f => f.wrapper === wrappers[i])) continue;

			wrappers[i].remove();
			wrappers.splice(i, 1);
		}

		for (let i = wrappers.length; i < count; i++) {
			const clone = template.cloneNode(true);
			clone.querySelector('.file-thumbnail')?.remove();
			clone.querySelector('.file-remove')?.remove();
			clone.querySelector('.file-pick')?.classList.remove('hidden');

			if (this.upload_more_wrapper) {
				this.upload_more_wrapper.before(clone);
			} else {
				this.upload_buttons_container.appendChild(clone);
			}
		}
	}

	/**
	 * Returns the shared "Add more" button, creating it (with its own wrapper) if the
	 * markup doesn't provide one.
	 * @returns {HTMLElement} The button element.
	 */
	_ensureUploadMoreButton() {
		let btn = this.upload_buttons_container.querySelector('.file-more');
		if (!btn) {
			const wrapper = document.createElement('div');
			wrapper.className = 'upload-button-wrapper';

			btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'file-more';
			btn.textContent = this.text_add_more_files;

			wrapper.appendChild(btn);
			this.upload_buttons_container.appendChild(wrapper);
		}
		return btn;
	}

	/**
	 * Returns a wrapper's "remove" button, creating it if the markup doesn't provide one.
	 * @param {HTMLElement} wrapper Wrapper element to ensure a remove button for.
	 * @returns {HTMLElement} The remove button element.
	 */
	_ensureUploadRemoveButton(wrapper) {
		let btn = wrapper.querySelector('.file-remove');
		if (!btn) {
			btn = document.createElement('button');
			btn.type = 'button';
			btn.className = 'file-remove';
			btn.setAttribute('aria-label', 'Remove file');
			btn.setAttribute('title', 'Remove file');
			wrapper.appendChild(btn);
		}
		return btn;
	}

	/**
	 * Finds the first wrapper slot that doesn't already hold a selected file.
	 * @returns {HTMLElement|null} The next empty wrapper, or null if all are filled.
	 */
	_nextEmptyUploadWrapper() {
		return this._getPickWrappers().find(w => !this.selected_upload_files.some(f => f.wrapper === w)) ?? null;
	}

	/**
	 * Opens the shared native file picker, allowing selection of as many images as
	 * there are remaining empty slots.
	 */
	_openUploadPicker() {
		if (this.selected_upload_files.length >= this.max_upload_files) return;

		this.upload_picker_input.click();
	}

	/**
	 * Distributes a batch of picked files across the remaining empty wrapper slots,
	 * ignoring any extra files once the max is reached.
	 * @param {File[]} files Picked image files.
	 */
	_addSelectedUploadFiles(files) {
		for (const file of files) {
			const wrapper = this._nextEmptyUploadWrapper();
			if (!wrapper) break;

			this._addSelectedUploadFile(file, wrapper);
		}
	}

	/**
	 * Stores the picked file for a wrapper, swaps its button for a thumbnail preview,
	 * and refreshes the surrounding controls' visibility. No upload request is made here.
	 * @param {File} file Picked image file.
	 * @param {HTMLElement|null} wrapper Wrapper slot the file belongs to.
	 */
	_addSelectedUploadFile(file, wrapper) {
		if (!wrapper || this.selected_upload_files.some(f => f.wrapper === wrapper)) return;
		if (this.selected_upload_files.length >= this.max_upload_files) return;

		const object_url = URL.createObjectURL(file);
		this.selected_upload_files.push({ file, url: object_url, wrapper });

		let thumbnail = wrapper.querySelector('.file-thumbnail');
		if (!thumbnail) {
			thumbnail = document.createElement('img');
			thumbnail.className = 'file-thumbnail';
			thumbnail.alt = '';
			wrapper.insertBefore(thumbnail, wrapper.firstChild);
		}
		thumbnail.src = object_url;

		wrapper.querySelector('.file-pick')?.classList.add('hidden');
		this._ensureUploadRemoveButton(wrapper).classList.remove('hidden');

		this._refreshUploadControlsVisibility();
	}

	/**
	 * Clears a wrapper's selected file and reverts it back to the empty pick state.
	 * @param {HTMLElement|null} wrapper Wrapper slot to clear.
	 */
	_removeSelectedUploadFile(wrapper) {
		if (!wrapper) return;

		const index = this.selected_upload_files.findIndex(f => f.wrapper === wrapper);
		if (index === -1) return;

		const [removed] = this.selected_upload_files.splice(index, 1);
		URL.revokeObjectURL(removed.url);

		wrapper.querySelector('.file-thumbnail')?.remove();
		wrapper.querySelector('.file-pick')?.classList.remove('hidden');
		wrapper.querySelector('.file-remove')?.classList.add('hidden');

		this._refreshUploadControlsVisibility();
	}

	/**
	 * Shows only the wrapper slots holding a selected file once at least one file is
	 * chosen (hiding the other empty pick buttons), and toggles the shared "Add more" button.
	 */
	_refreshUploadControlsVisibility() {
		const has_selection = this.selected_upload_files.length > 0;
		const can_add_more = has_selection && this.selected_upload_files.length < this.max_upload_files;

		this._getPickWrappers().forEach(wrapper => {
			const has_file = this.selected_upload_files.some(f => f.wrapper === wrapper);
			wrapper.classList.toggle('hidden', has_selection && !has_file);
		});

		this.upload_more_btn?.classList.toggle('hidden', !can_add_more);
		this.upload_more_wrapper?.classList.toggle('hidden', !can_add_more);

		this._refreshUploadSubmitState();
	}

	/**
	 * Enables the upload submit button only while at least one file is selected
	 * and no submission is already in progress.
	 */
	_refreshUploadSubmitState() {
		if (!this.upload_submit_btn) return;

		this.upload_submit_btn.disabled = !this.selected_upload_files.length || this._upload_submit_in_progress;
	}

	/**
	 * Resets all picked files/thumbnails back to the initial empty state.
	 */
	_resetUploadSelection() {
		if (!this.upload_buttons_container) return;

		this.selected_upload_files.forEach(f => URL.revokeObjectURL(f.url));
		this.selected_upload_files = [];

		this._getPickWrappers().forEach(wrapper => {
			wrapper.querySelector('.file-thumbnail')?.remove();
			wrapper.querySelector('.file-pick')?.classList.remove('hidden');
			wrapper.querySelector('.file-remove')?.classList.add('hidden');
			wrapper.classList.remove('hidden');
		});

		this.upload_more_btn?.classList.add('hidden');
		this.upload_more_wrapper?.classList.add('hidden');

		this._refreshUploadSubmitState();
		this._renderUploadConditions();
	}

	/**
	 * Writes the upload conditions text into `.upload-conditions`, filling the
	 * `{max_customer_uploads}` and `{max_upload_file_size}` placeholders with the
	 * number of images the user is allowed to upload and the maximum size of a single file.
	 */
	_renderUploadConditions() {
		if (!this.upload_label_el) return;

		this.upload_label_el.innerHTML = this.text_upload_conditions
			.replaceAll('{max_customer_uploads}', this.max_upload_files)
			.replaceAll('{max_upload_file_size}', this._formatFileSize(this.max_upload_file_size));
	}

	/**
	 * Converts a byte count to a short human-readable size (e.g. 8388608 -> "8MB").
	 * @param {number} bytes Size in bytes.
	 * @returns {string} Formatted size.
	 */
	_formatFileSize(bytes) {
		const units = ['B', 'KB', 'MB', 'GB'];
		let size = Number(bytes) || 0;
		let unit = 0;

		while (size >= 1024 && unit < units.length - 1) {
			size /= 1024;
			unit++;
		}

		return `${Math.round(size * 10) / 10}${units[unit]}`;
	}

	/**
	 * Uploads every currently selected file to the live chat, one at a time
	 * (mirroring `handleFileUploadAction`'s single-file bubble/thumbnail flow
	 * for each). Once every file the user intended to send in this upload cycle
	 * has gone through, the uploader is disabled server-side; if any failed it
	 * stays open so the user can retry.
	 */
	async _submitSelectedUploads() {
		if (!this.selected_upload_files.length || this._upload_submit_in_progress) return;

		this._upload_submit_in_progress = true;

		const files = this.selected_upload_files.map(f => f.file);
		this._resetUploadSelection();

		// a retry after a partial failure counts towards the original intent
		const cycle = this._upload_cycle;
		cycle.intended = Math.max(cycle.intended, cycle.succeeded + files.length);

		for (const file of files) {
			// failures are already reported per file in the chat log
			if (await this.handleFileUploadAction(file)) cycle.succeeded++;
		}

		this._upload_submit_in_progress = false;
		this._refreshUploadSubmitState();

		if (cycle.succeeded >= cycle.intended) await this._disableCustomerUpload();
	}

	/**
	 * Forgets the upload cycle and server upload state, e.g. when the conversation ends.
	 */
	_resetUploadCycle() {
		this._upload_cycle = { intended: 0, succeeded: 0 };
		this._upload_server_enabled = null;
		this._upload_disable_pending = false;
		this._file_upload_server_available = false;
	}

	/**
	 * Hides the uploader and asks the backend to disable customer uploads for this
	 * chat, closing the current upload cycle. Only the agent can re-enable it.
	 */
	async _disableCustomerUpload() {
		this._upload_cycle = { intended: 0, succeeded: 0 };
		this._upload_disable_pending = true;
		this._file_upload_server_available = false;
		this._refreshFileUploadVisibility();

		try {
			const response = await this._post('live_disable_upload');
			if (!response?.ok) throw new Error('live_disable_upload rejected');
		} catch (e) {
			console.error('ChatSupport::_disableCustomerUpload failed', e);
			// let the next poll's server state drive the uploader again
			this._upload_disable_pending = false;
		}
	}

	/**
	 * Shows the typing indicator after a specified delay.
	 * @param {number} delay_ms Delay before appending the indicator.
	 * @param {Function} [after_callback] Optional callback executed after insertion.
	 * @returns {number} Timeout ID.
	 */
	_showTypingIndicator(delay_ms, after_callback) {
		return setTimeout(() => {
			this.history.insertAdjacentHTML('beforeend', this.typing_indicator);
			this._scrollToBottom();
			after_callback?.();
		}, delay_ms);
	}

	/**
	 * Appends the searching-for-agent indicator to the chat history and locks input.
	 */
	_showSearchingForAgentIndicator() {
		if (this.history.querySelector('.searching-indicator')) return;

		this.awaiting_agent_connection = true;
		this.setInputState(false);

		this.searching_shown_at = Date.now();
		this.history.insertAdjacentHTML('beforeend', this.searching_indicator);
		this._scrollToBottom();
	}

	/**
	 * Immediately inserts the typing indicator without delay.
	 */
	_showTypingIndicatorNow() {
		if (this.history.querySelector('.typing-indicator')) return;
		this.history.insertAdjacentHTML('beforeend', this.typing_indicator);
		this._scrollToBottom();
	}
	
	/**
	 * Removes all searching-for-agent indicators from the history view.
	 */
	_removeSearchingIndicator() {
		this.history.querySelectorAll('.searching-indicator').forEach(el => el.remove());
	}

	/**
	 * Removes all typing indicators from the chat history.
	 */
	_removeTypingIndicators() {
		this.history.querySelectorAll('.typing-indicator').forEach(el => el.remove());
	}

	/**
	 * Clears the chat history and resets the greeting bubble.
	 */
	_resetHistoryDom() {
		this.history.querySelectorAll('.message:not(.initial)').forEach(msg => msg.remove());
		this._removeTypingIndicators();
		this._removeSearchingIndicator();

		this.agent_notice_shown = false;
		this.escalated = false;

		this.history.querySelectorAll('.chat-loading-indicator').forEach(el => el.remove());
		if (this.initial_message) {
			this.initial_message.style.transition = '';
			this.initial_message.style.opacity = '';
			this.initial_message.style.display = '';
		}
		if (this.clear_conversation_btn) this.clear_conversation_btn.disabled = false;
	}

	/**
	 * Scrolls the chat history panel to the most recent message.
	 */
	_scrollToBottom() {
		this.history.scrollTop = this.history.scrollHeight;
	}

	/**
	 * Re-scrolls to bottom once the attachment thumbnail finishes loading
	 */
	_rescrollOnThumbnailLoad() {
		const img = this.history.lastElementChild?.querySelector('.attachment-thumbnail img');
		if (!img) return;

		if (img.complete) {
			this._scrollToBottom();
		} else {
			img.addEventListener('load', () => this._scrollToBottom(), { once: true });
		}
	}

	/**
	 * Clears active polling timers and resets in-flight polling states.
	 */
	_stopPolling() {
		clearTimeout(this.poll_timer);
		this.poll_timer = null;
		this.polling_in_flight = false;
	}

	/**
	 * Triggers the live agent polling loop if not currently running or stopped.
	 */
	_startPolling() {
		if (this.poll_timer || this.polling_stopped || this.polling_in_flight) return;
		this._poll();
	}

	/**
	 * Executes a server poll for live chat updates and reschedules the loop.
	 */
	async _poll() {
		if (this.polling_in_flight) return;
		this.polling_in_flight = true;

		try {
			const session = this.getStoredSession();

			const log = await this._post('live_refresh_log', {
				date: Number.isFinite(this.last_update) ? this.last_update : 0
			});
			this._renderChatLog(log?.log);
			this._updateQueuePosition(log?.queue_position);
			this._updateFileUploadAvailability(log);
			this._handleSessionStatus(log?.status);
			this._checkTimeoutWarning();

			if (session?.conversation_id) {
				this.refreshSession();
			}
		} catch (e) {
			console.error('ChatSupport::poll failed', e);
		} finally {
			this.polling_in_flight = false;
		}

		if (!this.polling_stopped) {
			const interval = this.agent_joined
				? ChatSupport.POLL_INTERVAL_ACTIVE
				: ChatSupport.POLL_INTERVAL_SEARCHING;

			this.poll_timer = setTimeout(() => this._poll(), interval);
		}
	}

	/**
	 * Sorts and appends log entries to the view, then scrolls to bottom.
	 * @param {Array} log List of log event objects from the server.
	 */
	_renderChatLog(log) {
		if (!Array.isArray(log) || !log.length) return;

		log.sort((a, b) => Number(a.date) - Number(b.date));
		log.forEach(item => this._appendLogEntry(item));
		this._scrollToBottom();
	}

	/**
	 * Parses a single log event item and updates the UI/state accordingly.
	 * @param {Object} item Individual log entry item from the server.
	 */
	_appendLogEntry(item) {
		switch (item.type) {
			case 'text_customer': {
				if (this.pending_sent_count > 0) {
					this.pending_sent_count--;
				} else {
					this.history.insertAdjacentHTML('beforeend', this.createBubble('user', item.message, [], '', item.date));
				}
				if (!this.agent_joined) {
					this._showSearchingForAgentIndicator();
				}
				break;
			}

			case 'text_agent': {
				this._removeTypingIndicators();
				this._removeSearchingIndicator();
				this.awaiting_agent_connection = false;
				this.setInputState(true);

				this.agent_joined = true;
				this.history.insertAdjacentHTML('beforeend', this.createBubble('bot', item.message, [], item.name || '', item.date));
				break;
			}

			case 'file_upload_customer': {
				if (this.pending_upload_count > 0) {
					this.pending_upload_count--;
				} else {
					this.history.insertAdjacentHTML('beforeend', this.createBubble('user', item.message, [], '', item.date));
					this._rescrollOnThumbnailLoad();
				}
				if (!this.agent_joined) {
					this._showSearchingForAgentIndicator();
				}
				break;
			}

			case 'file_upload_agent': {
				this._removeTypingIndicators();
				this._removeSearchingIndicator();
				this.awaiting_agent_connection = false;
				this.setInputState(true);
				this.agent_joined = true;
				this.history.insertAdjacentHTML('beforeend', this.createBubble('bot', item.message, [], item.name || '', item.date));
				this._rescrollOnThumbnailLoad();
				break;
			}

			case 'new': {
				if (item.escalated) {
					this.escalated = true;
				}
				break;
			}

			case 'active': {
				this.agent_joined = true;
				this.awaiting_agent_connection = false;
				this.setInputState(true);

				if (!_production) console.log('ChatSupport::_appendLogEntry active(%o)', item);

				const elapsed = Date.now() - (this.searching_shown_at || 0);
				const remaining = Math.max(0, ChatSupport.MIN_SEARCHING_DISPLAY_MS - elapsed);
				setTimeout(() => this._removeSearchingIndicator(), remaining);
				this._showAgentAssignedGreeting(item);
				break;
			}

			case 'agent_typing_text': {
				if (!_production) console.log('ChatSupport::_appendLogEntry agent_typing_text(%o)', item);
				this._removeSearchingIndicator();
				this.agent_joined = true;
				if (item.agent_typing) {
					this._showTypingIndicatorNow();
				} else {
					this._removeTypingIndicators();
				}
				break;
			}

			case 'closed':
			case 'failed': {
				this._removeTypingIndicators();
				this._removeSearchingIndicator();

				if (this.polling_stopped) {
					console.warn('ChatSupport: ignoring redundant closed/failed entry — conversation already ended', item);
					break;
				}

				if (item.type === 'failed' && this.agent_joined) {
					console.warn('ChatSupport: ignoring stale failed entry — agent already joined', item);
					break;
				}

				const had_agent = this.agent_joined;
				this._endConversation();

				this._showConversationEndedPrompt(had_agent, item.type === 'failed');
				break;
			}
		}

		const date_num = Number(item.date);
		if (item.date !== undefined && item.date !== null && Number.isFinite(date_num)) {
			this.last_update = date_num;
		} else {
			console.warn('ChatSupport: log entry missing valid date, keeping previous cursor', item);
		}

		if (typeof this.last_update !== 'number' || Number.isNaN(this.last_update)) {
			console.warn('ChatSupport: received log entry with invalid date, keeping previous last_update', item);
			this.last_update = this._last_valid_update ?? 0;
		} else {
			this._last_valid_update = this.last_update;
		}

		if (['text_customer', 'text_agent', 'text_chatbot'].includes(item.type)) {
			this.last_activity_date = item.date;
			this.timeout_warning_shown = false;
		}
	}

	/**
	 * Stops conversation activities, disables inputs, and clears storage/session data.
	 */
	_endConversation() {
		this._resetUploadSelection();
		this.polling_stopped = true;
		this._stopPolling();
		this._removeTypingIndicators();
		this._removeSearchingIndicator();
		this.interaction.classList.add('disabled');
		this.awaiting_agent_connection = false;
		localStorage.removeItem(ChatSupport.STORAGE_KEY);
		this.transcript = [];
		this.agent_joined = false;
		this.agent_notice_shown = false;
		this.escalated = false;
		this.stop_conversation_btn?.classList.add('hidden');
		this._file_upload_is_live = false;
		this._resetUploadCycle();
		this._refreshFileUploadVisibility();
		this._setKeepAliveVisible(false);
	}

	_showAgentAssignedGreeting(item) {
		if (this.agent_notice_shown || !this.escalated || !this.text_live_agent_assigned) return;

		this.agent_notice_shown = true;
		this._appendBubble('bot', this.text_live_agent_assigned, [], item.name || '', item.date);
	}

	/**
	 * Displays the appropriate post-chat prompt depending on how the session ended.
	 * @param {boolean} had_agent Whether an agent was ever attached to the conversation.
	 * @param {boolean} [is_failed] True if session failed because no agent was available.
	 */
	_showConversationEndedPrompt(had_agent, is_failed = false) {
		if (is_failed) {
			this._showNoAgentsPrompt();
		} else if (had_agent) {
			this._showAgentEndedPrompt();
		} else {
			this._showBotEndedPrompt();
		}
	}

	/**
	 * Displays restart prompt when an AI-only conversation ends.
	 */
	_showBotEndedPrompt() {
		this.setInputState(false);

		this._appendBubble(
			'bot event conversation-ended-prompt',
			this.text_conversation_ended_chatbot,
			[{ title: this.text_button_restart_conversation, action: 'bot-ended-restart' }]
		);
	}

	/**
	 * Renders prompt indicating no live agents were available to pick up the chat.
	 */
	_showNoAgentsPrompt() {
		this._appendBubble(
			'bot event no-agents-prompt',
			this.text_chat_support_msg_all_busy,
			[{ title: this.text_button_create_support_ticket, action: 'no-agents-acknowledge' }]
		);
	}
	
	/**
	 * Handles no-agent acknowledgment, sends escalation skip request, and displays status.
	 */
	async _onNoAgentsAcknowledge() {
		this._logEvent('text_customer', this.text_button_create_support_ticket);

		let ticket_filed = false;
		try {
			const response = await this._post('live_skip_waiting_for_agent');
			ticket_filed = !!response?.ok;
		} catch (e) {
			console.error('ChatSupport::_onNoAgentsAcknowledge failed', e);
		}

		const message = ticket_filed
			? this.text_escalation_trough_email
			: this.text_transcript_send_error;

		this._appendBubble(
			'bot event conversation-ended-prompt',
			message,
			[{ title: this.text_button_restart_conversation, action: 'bot-ended-restart' }]
		);
	}

	/**
	 * Shows prompt offering to email transcript after a live agent chat ends.
	 */
	_showAgentEndedPrompt() {
		this.setInputState(false);

		this._appendBubble(
			'bot event email-transcript-prompt',
			this.text_conversation_ended_agent,
			[
				{ title: this.text_send_transcript_yes_button, action: 'email-transcript-yes' },
				{ title: this.text_send_transcript_no_button, action: 'email-transcript-no' }
			]
		);

		this.awaiting_clear_confirmation = true;
	}

	/**
	 * Processes the user choice regarding emailing the chat transcript.
	 * @param {boolean} wants_email True if user requested transcript email.
	 */
	async _onEmailTranscriptResponse(wants_email) {
		this.awaiting_clear_confirmation = false;
		this.history.querySelector('.message.email-transcript-prompt')?.classList.remove('email-transcript-prompt');

		this._appendBubble(
			'user',
			wants_email ? this.text_send_transcript_yes_button : this.text_send_transcript_no_button
		);

		if (!wants_email) {
			await this._resetServerSession();

			this._appendBubble(
				'bot event conversation-ended-prompt',
				this.text_conversation_ended_chatbot,
				[{ title: this.text_button_start_conversation, action: 'bot-ended-restart' }]
			);
			return;
		}

		try {
			const response = await this._post('live_email_transcript');

			const message = response?.error
				? this.text_transcript_send_error
				: this.text_thank_you_for_chatting;

			this._appendBubble(
				'bot event',
				message,
				[{ title: this.text_button_start_conversation, action: 'start-new-conversation' }]
			);

			this.setInputState(true);
		} catch (e) {
			console.error('ChatSupport::live_email_transcript failed', e);

			const errorMessage = !_production
				? `⚠️ ${this.text_transcript_send_error} (E1): ${e.message || e}`
				: `⚠️ ${this.text_transcript_send_error}`;
			this._appendBubble('bot error', errorMessage);
		}
	}

	/**
	 * Sends a user message to either the AI or live agent endpoint and renders the server response.
	 * @param {string} message Message text to send to backend.
	 * @param {string} [display_message] Message string to display inside the rendered chat bubble.
	 */
	async sendMessage(message, display_message = message) {
		if (!_production) console.log('ChatSupport::sendMessage sending message to support:', message);

		const session = this.getStoredSession();
		const is_live = session?.mode === 'live' && !!session.conversation_id;

		this._appendBubble('user', this.escapeHtml(display_message));

		if (!is_live) {
			this.transcript.push({ role: 'user', content: display_message, buttons: [] });
			this.saveSession({ mode: 'ai', transcript: this.transcript });
		}

		if (is_live) this.pending_sent_count++;

		this.textarea.value = '';
		this.textarea.style.height = ChatSupport.MIN_TEXTAREA_HEIGHT + 'px';

		this.setInputState(false);

		const typing_delay = is_live ? null : this._showTypingIndicator(400);

		try {
			const used_live_endpoint = is_live;
			const response = used_live_endpoint
				? await this._sendLiveMessage(message)
				: await this._sendAiMessage(message);

			clearTimeout(typing_delay);
			if (!is_live) {
				this._removeTypingIndicators();
			}
			this._removeSearchingIndicator();

			if (!response || response.error) {
				const errorMessage = !_production
					? `⚠️ ${this.text_port_in_api_call_failure} (E2): ${response.error?.message || response.error || 'Unknown error'}`
					: `⚠️ ${this.text_port_in_api_call_failure}`;
				this._appendBubble('bot error', errorMessage);
				return;
			}

			if (response.message || response.buttons?.length) {
				this._appendBubble(
					'bot',
					response.message || '',
					response.buttons || []
				);

				if (!is_live && !response.escalated) {
					this.transcript.push({
						role: 'bot',
						content: response.message || '',
						buttons: response.buttons || [],
						senderName: this.bot_display_name
					});

					this.saveSession({
						mode: 'ai',
						transcript: this.transcript,
						chatbot_session: this.chatbot_session,
						conversation_id: this.conversation_id,
						last_chatbot_date: this.last_chatbot_date
					});
				}
			}

			this.awaiting_quick_reply = !!response.buttons?.length;

			if (response.no_agents) {
				this._showTypingIndicator(400, () => {
					this._removeTypingIndicators();
					this._appendBubble(
						'bot event no-agents-prompt',
						response.no_agents_message,
						[{ title: this.text_button_create_support_ticket, action: 'no-agents-acknowledge' }]
					);
					this._endConversation();
				});
				return;
			}

			if (response.conversation_ended) {
				this._showBotEndedPrompt();
				this._endConversation();
				return;
			}

			if (response.conversation_id && (used_live_endpoint || response.escalated)) {
				if (!_production) console.log('ChatSupport::sendMessage saving session(%o)', response.conversation_id);

				if (response.escalated) {
					if (response.message) {
						this.transcript.push({
							role: 'bot',
							content: response.message,
							buttons: [],
							senderName: this.bot_display_name
						});
					}

					this.saveSession({
						mode:              'live',
						conversation_id:   String(response.conversation_id),
						chatbot_session:   null,
						transcript:        this.transcript,
						last_chatbot_date: this.last_chatbot_date
					});

					this._updateToolbarButtons();
					this.last_update = this.last_chatbot_date || 0;
					this.pending_sent_count++;
					this.stop_conversation_btn?.classList.remove('hidden');
				} else {
					this.refreshSession();
				}

				this._startPolling();
				if (!this.agent_joined) {
					this._showSearchingForAgentIndicator();
				}
			}
		} catch (e) {
			clearTimeout(typing_delay);
			this._removeTypingIndicators();

			if (is_live) this.pending_sent_count--;

			console.error('ChatSupport::sendMessage failed', e);

			const errorMessage = !_production
				? `⚠️ ${this.text_port_in_api_call_failure} (E3): ${e.message || e}`
				: `⚠️ ${this.text_port_in_api_call_failure}`;
			this._appendBubble('bot error', errorMessage);
		} finally {
			if (!this.polling_stopped) {
				this.setInputState(true);
			}
		}
	}

	/**
	 * Sends a chat message to the live agent endpoint.
	 * @param {string} message Text content.
	 * @returns {Promise<Object>} Fetch response data.
	 */
	async _sendLiveMessage(message) {
		return await this._post('live_send_message', { message });
	}

	/**
	 * Ensures AI chatbot authorization token exists, requesting a new one if missing.
	 * @returns {Promise<void>}
	 */
	_ensureChatbotAuthorized() {
		if (this.chatbot_authorized) return Promise.resolve();
		if (this.chatbot_authorize_promise) return this.chatbot_authorize_promise;

		const session = this.getStoredSession();

		if (session?.mode === 'live') return Promise.resolve();

		if (session?.chatbot_session) {
			this.chatbot_session = session.chatbot_session;
			this.chatbot_authorized = true;
			return Promise.resolve();
		}

		this.chatbot_authorize_promise = (async () => {
			try {
				const auth = await this._post('chatbot_authorize', {
					offset: new Date().getTimezoneOffset()
				});
				if (!_production) console.log('ChatSupport::chatbot_authorize response(%o)', auth);
				this.chatbot_authorized = !!auth?.authorized;

				if (auth?.chatbot_token && auth?.chatbot_conversation_id) {
					this.chatbot_session = { token: auth.chatbot_token, conversation_id: auth.chatbot_conversation_id };

					const session_patch = { mode: 'ai', chatbot_session: this.chatbot_session };

					if (auth?.conversation_id) {
						this.conversation_id = String(auth.conversation_id);
						session_patch.conversation_id = this.conversation_id;
					}

					this.saveSession(session_patch);
				}
			} catch (e) {
				console.error('ChatSupport::_ensureChatbotAuthorized failed', e);
			} finally {
				this.chatbot_authorize_promise = null;
			}
		})();

		return this.chatbot_authorize_promise;
	}

	/**
	 * Runs the loading indicator and fade-in animation sequence for a new chat greeting.
	 * @returns {Promise<void>}
	 */
	async _showInitialGreeting() {
		if (this.greeting_in_progress) return;
		this.greeting_in_progress = true;

		const MIN_LOADING_MS = 700;

		try {
			this.interaction.classList.add('disabled');
			this.setInputState(false);

			if (this.clear_conversation_btn) this.clear_conversation_btn.disabled = true;

			this.history.querySelectorAll('.chat-loading-indicator').forEach(el => el.remove());

			if (this.initial_message) {
				this.initial_message.style.transition = 'none';
				this.initial_message.style.opacity = '0';
				this.initial_message.style.display = 'none';
				this.initial_message.insertAdjacentHTML('beforebegin', this.loading_indicator);
			} else {
				this.history.insertAdjacentHTML('afterbegin', this.loading_indicator);
			}
			this._scrollToBottom();

			const started_at = Date.now();
			await this._ensureChatbotAuthorized();
			const remaining_ms = MIN_LOADING_MS - (Date.now() - started_at);
			if (remaining_ms > 0) {
				await new Promise(resolve => setTimeout(resolve, remaining_ms));
			}

			this.history.querySelectorAll('.chat-loading-indicator').forEach(el => el.remove());

			if (this.initial_message) {
				this.initial_message.style.display = '';
				this.initial_message.style.transition = 'opacity 400ms ease';
				void this.initial_message.offsetWidth;
				this.initial_message.style.opacity = '1';
			}

			this.interaction.classList.remove('disabled');
			this.setInputState(true);
			if (this.clear_conversation_btn) this.clear_conversation_btn.disabled = false;
			this._scrollToBottom();
		} finally {
			this.greeting_in_progress = false;
		}
	}

	/**
	 * Authorizes session if needed and posts a message to the AI chatbot API.
	 * @param {string} message Text message string.
	 * @returns {Promise<Object>} API response object.
	 */
	async _sendAiMessage(message) {
		await this._ensureChatbotAuthorized();

		const response = await this._post('chatbot_send_message', {
			message: message,
			chatbot_token: this.chatbot_session?.token ?? '',
			chatbot_conversation_id: this.chatbot_session?.conversation_id ?? ''
		});
		if (response?.chatbot_token && response?.chatbot_conversation_id) {
			this.chatbot_session = { token: response.chatbot_token, conversation_id: response.chatbot_conversation_id };
		}

		if (response?.conversation_id) {
			this.conversation_id = String(response.conversation_id);
		}

		if (response?.server_time) {
			this.last_chatbot_date = response.server_time;
		}

		if (response?.escalated) {
			this.chatbot_session = null;
		}

		return response;
	}

	/**
	 * Escapes dynamic content strings for safe HTML rendering.
	 * @param {string} str Input string.
	 * @returns {string} Escaped HTML string.
	 */
	escapeHtml(str) {
		const div = document.createElement('div');
		div.textContent = str;
		return div.innerHTML;
	}

	/**
	 * Builds HTML for a set of quick-reply buttons.
	 * @param {Array<Object>} buttons Button data.
	 * @returns {string} Button HTML.
	 */
	_buildQuickReplyButtonsHtml(buttons) {
		return buttons.map(b =>
			`<button type="button" class="quick-reply btn" data-value="${b.value ?? ''}" data-action="${b.action ?? ''}">${b.title}</button>`
		).join('');
	}

	/**
	 * Constructs and returns HTML markup for a chat bubble element.
	 * @param {string} role Message type/style role.
	 * @param {string} content Bubble text/HTML content.
	 * @param {Array<Object>} [buttons] Optional quick-reply buttons array.
	 * @param {string} [senderName] Optional sender name.
	 * @param {number|null} [timestamp] Unix timestamp in seconds.
	 * @returns {string} HTML string of formatted bubble.
	 */
	createBubble(role, content, buttons = [], senderName = '', timestamp = null) {
		const buttons_html = buttons.length
			? `<div class="quick-replies">${this._buildQuickReplyButtonsHtml(buttons)}</div>`
			: '';

		const avatar = role.includes('bot')
			? `<div class="avatar ${senderName && senderName !== this.bot_display_name ? 'agent' : ''}">
				<img src="${url(`/images/tello/druid/${senderName && senderName !== this.bot_display_name ? 'agent-profile.svg' : 'bot-profile.png'}`)}" alt="" />
			</div>`
			: '';

		const display_time = timestamp ? new Date(timestamp * 1000) : new Date();

		return `<div class="message x-body-md ${role}">
			<div class="message-row">
				${avatar}
				<div class="bubble">
					${senderName ? `<span class="sender">${this.escapeHtml(senderName)}</span>` : ''}
					<div>
						${content}
					</div>
					${buttons_html}
				</div>
			</div>
			<div class="message-row">
				<div class="col-placeholder"></div>
				<div class="col-events">
					<span class="timestamp">${display_time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
				</div>
			</div>
		</div>`;
	}

	/**
	 * Appends a chat bubble to the history and scrolls to it.
	 * Accepts the same arguments as `createBubble`.
	 */
	_appendBubble(...args) {
		this.history.insertAdjacentHTML('beforeend', this.createBubble(...args));
		this._scrollToBottom();
	}

	/**
	 * Sets the disabled state and focus status for message input elements.
	 * @param {boolean} enabled True if inputs should accept interaction.
	 */
	setInputState(enabled) {
		const effective = enabled && !this.awaiting_quick_reply && !this.awaiting_clear_confirmation && !this.awaiting_agent_connection;
		this.textarea.disabled = !effective;
		this.interaction.querySelector('button[type="submit"]').disabled = !effective;
		this.interaction.classList.toggle('disabled', !effective);
		if (effective) this.textarea.focus();
	}

	/**
	 * Closes widget visual elements and halts background activity.
	 * @param {boolean} [mark_closed_by_user=false] Flag to remember manual closure.
	 */
	closeChat(mark_closed_by_user = false) {
		this.widget.classList.remove('open');
		document.body.classList.remove('chat-support-open');

		this._stopPolling();

		if (mark_closed_by_user) {
			this.markSessionClosedByUser();
		}
	}

	/**
	 * Restores previous conversation state from local storage on load or reopen.
	 */
	async resumeOrStartConversation() {
		const session = this.getStoredSession();
		if (!session) return;

		this._stopPolling();
		this.conversation_id = session.conversation_id;

		this._updateToolbarButtons();

		this._resetHistoryDom();

		try {
			if (session.mode === 'live' && session.conversation_id) {
				const ai_boundary = await this._loadAiPhaseLog(true, session.last_chatbot_date || null);

				if (this.polling_stopped) return;

				await this.initConversation(session.conversation_id, ai_boundary);
			} else if (session.mode === 'ai' && session.chatbot_session) {
				this.chatbot_session = session.chatbot_session;
				this.conversation_id = session.conversation_id;
				this.last_chatbot_date = session.last_chatbot_date || 0;
				this.chatbot_authorized = true;

				const response = await this._fetchAiPhaseLog();
				const log = Array.isArray(response?.log) ? response.log : [];

				if (this._logIndicatesEscalation(log)) {
					await this._healStaleAiSession(session.conversation_id, log, response);
					return;
				}

				this._renderAiPhaseLog(log, false);
				this._handleSessionStatus(response?.status);
				if (this.polling_stopped) return;

				this._restorePendingQuickReply(session.transcript);
				this.interaction.classList.remove('disabled');
				this.setInputState(true);
				this._scrollToBottom();

				this._awaitPendingAiReply(session.conversation_id);
			}
		} catch (e) {
			console.error('ChatSupport::resumeOrStartConversation failed', e);
		}
	}

	/**
	 * Checks if log contains active agent markers indicating backend escalation.
	 * @param {Array} log List of log entry items.
	 * @returns {boolean} True if live agent markers exist.
	 */
	_logIndicatesEscalation(log) {
		const LIVE_PHASE_MARKERS = ['active', 'text_agent', 'agent_typing_text'];
		return log.some(item => LIVE_PHASE_MARKERS.includes(item.type));
	}

	/**
	 * Converts an out-of-sync AI mode session to live mode and restores live chat.
	 * @param {string} conversation_id Active conversation identifier.
	 * @param {Array} log Log entries already retrieved.
	 * @param {Object|null} response Response context containing status information.
	 */
	async _healStaleAiSession(conversation_id, log, response) {
		console.warn('ChatSupport: session.mode was stale ("ai" but already escalated server-side) — correcting to "live"');

		const last_chatbot_entry = [...log].reverse().find(item => item.type === 'text_chatbot');
		const session = this.getStoredSession();
		const boundary_date = last_chatbot_entry?.date
			? Number(last_chatbot_entry.date)
			: (session?.last_chatbot_date || null);

		this.chatbot_session = null;
		this.last_chatbot_date = boundary_date || 0;
		this.saveSession({
			mode: 'live',
			conversation_id,
			chatbot_session: null,
			last_chatbot_date: boundary_date || 0
		});
		this._updateToolbarButtons();

		this.history.querySelectorAll('.message:not(.initial)').forEach(msg => msg.remove());
		this._removeTypingIndicators();

		this.setInputState(true);

		const ai_boundary = this._renderAiPhaseLog(log, true, boundary_date);
		this._handleSessionStatus(response?.status);
		if (this.polling_stopped) return;

		await this.initConversation(conversation_id, ai_boundary);
	}

	/**
	 * Periodically checks server for pending AI responses following page refreshes.
	 * @param {string} conversation_id Current conversation ID.
	 * @param {number} [attempt=0] Iteration attempt count.
	 */
	async _awaitPendingAiReply(conversation_id, attempt = 0) {
		const MAX_ATTEMPTS = 5;
		const RETRY_DELAY_MS = 2500;

		const messages = this.history.querySelectorAll('.message:not(.initial)');
		const last_bubble = messages[messages.length - 1];
		const still_waiting = last_bubble?.classList.contains('user') && !this.awaiting_quick_reply;

		if (!still_waiting || attempt >= MAX_ATTEMPTS || this.polling_stopped) {
			this._endAiReplyWait();
			return;
		}

		if (attempt === 0) {
			this._showTypingIndicatorNow();
			this.setInputState(false);
		}

		await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));

		const session = this.getStoredSession();
		if (!session || session.mode !== 'ai' || session.conversation_id !== conversation_id || this.polling_stopped) {
			this._endAiReplyWait();
			return;
		}

		const response = await this._fetchAiPhaseLog();
		this._handleSessionStatus(response?.status);
		if (this.polling_stopped) return;
		const log = Array.isArray(response?.log) ? response.log : [];

		const last_turn = [...log]
			.sort((a, b) => Number(a.date) - Number(b.date))
			.filter(item => item.type === 'text_customer' || item.type === 'text_chatbot')
			.pop();

		if (last_turn?.type === 'text_chatbot') {
			this._removeTypingIndicators();

			if (this._logIndicatesEscalation(log)) {
				await this._healStaleAiSession(conversation_id, log, response);
				return;
			}

			this.history.querySelectorAll('.message:not(.initial)').forEach(msg => msg.remove());
			this._renderAiPhaseLog(log, false);
			this._restorePendingQuickReply(session.transcript);
			this._scrollToBottom();
			this.setInputState(true);
			return;
		}

		this._awaitPendingAiReply(conversation_id, attempt + 1);
	}

	/**
	 * Clears typing indicators and restores input controls after waiting for an AI reply.
	 */
	_endAiReplyWait() {
		this._removeTypingIndicators();
		if (!this.polling_stopped) {
			this.setInputState(true);
		}
	}

	/**
	 * Re-renders quick reply buttons on the last message using local transcript data.
	 * @param {Array} transcript Cached transcript log entries.
	 */
	_restorePendingQuickReply(transcript) {
		const last = transcript?.at(-1);
		if (!last || last.role !== 'bot' || !last.buttons?.length) return;

		const bubble = this.history.querySelector('.message:last-child .bubble');
		if (!bubble) return;

		const buttons_html = this._buildQuickReplyButtonsHtml(last.buttons);
		bubble.insertAdjacentHTML('beforeend', `<div class="quick-replies">${buttons_html}</div>`);

		this.awaiting_quick_reply = true;
	}

	/**
	 * Fetches the raw log data for the AI phase of the current conversation from backend.
	 * @returns {Promise<Object|null>} Response data object or null on failure.
	 */
	async _fetchAiPhaseLog() {
		try {
			const response = await this._post('live_refresh_log', { date: 0 });
			this._updateFileUploadAvailability(response);
			return response;
		} catch (e) {
			return null;
		}
	}

	/**
	 * Renders pre-escalation AI log entries into the DOM up to the cutoff boundary.
	 * @param {Array} log Unfiltered backend log items.
	 * @param {boolean} [has_escalated=false] Whether session is in live mode.
	 * @param {number|null} [boundary_date=null] Timestamp limit for AI items.
	 * @returns {number} Resolved boundary timestamp.
	 */
	_renderAiPhaseLog(log, has_escalated = false, boundary_date = null) {
		const entries = has_escalated && boundary_date != null
			? log.filter(item => !item.date || Number(item.date) <= boundary_date)
			: log;

		const last_chatbot_index = has_escalated ? entries.map(i => i.type).lastIndexOf('text_chatbot') : -1;

		let resolved_boundary = boundary_date ?? 0;
		entries.forEach((item, index) => {
			if (item.type === 'text_chatbot' && index === last_chatbot_index) {
				if (item.date) resolved_boundary = Number(item.date);
				return;
			}

			if (item.type === 'active' || item.type === 'text_agent' || item.type === 'agent_typing_text') {
				this.agent_joined = true;
			}

			if (item.type === 'new' && item.escalated) {
				this.escalated = true;
			}

			if (['text_customer', 'text_agent', 'text_chatbot'].includes(item.type) && item.date) {
				this.last_activity_date = Math.max(this.last_activity_date || 0, Number(item.date));
				this.timeout_warning_shown = false;
			}

			if (item.type === 'text_customer') {
				this.history.insertAdjacentHTML('beforeend', this.createBubble('user', item.message, [], '', item.date));
			} else if (item.type === 'text_chatbot') {
				this.history.insertAdjacentHTML('beforeend', this.createBubble('bot', item.message, [], '', item.date));
				if (item.date) resolved_boundary = Math.max(resolved_boundary, Number(item.date));
			}
		});

		return resolved_boundary;
	}

	/**
	 * Loads and renders the historical AI portion of the current conversation log.
	 * @param {boolean} [has_escalated=false] Escalation status flag.
	 * @param {number|null} [boundary_date=null] Time boundary for filtering entries.
	 * @returns {Promise<number>} Resolved boundary timestamp.
	 */
	async _loadAiPhaseLog(has_escalated = false, boundary_date = null) {
		const response = await this._fetchAiPhaseLog();
		const log = Array.isArray(response?.log) ? response.log : [];
		const resolved_boundary = this._renderAiPhaseLog(log, has_escalated, boundary_date);

		this._handleSessionStatus(response?.status);

		return resolved_boundary;
	}

	/**
	 * Reads active session object from localStorage if valid and non-expired.
	 * @returns {Object|null} Valid stored session object or null.
	 */
	getStoredSession() {
		try {
			const raw = localStorage.getItem(ChatSupport.STORAGE_KEY);
			if (!raw) return null;

			const session = JSON.parse(raw);

			if (Date.now() - session.timestamp > ChatSupport.CONVERSATION_TTL) {
				localStorage.removeItem(ChatSupport.STORAGE_KEY);
				return null;
			}

			const conversation_id = (typeof session.conversation_id === 'string' || typeof session.conversation_id === 'number')
				&& session.conversation_id !== ''
				? String(session.conversation_id)
				: null;

			return {
				mode:              session.mode === 'live' ? 'live' : 'ai',
				conversation_id,
				chatbot_session:   (session.chatbot_session?.token && session.chatbot_session?.conversation_id)
					? { token: session.chatbot_session.token, conversation_id: session.chatbot_session.conversation_id }
					: null,
				transcript:        Array.isArray(session.transcript) ? session.transcript : [],
				last_chatbot_date: typeof session.last_chatbot_date === 'number' ? session.last_chatbot_date : 0,
				pending_confirmation: typeof session.pending_confirmation === 'string' ? session.pending_confirmation : null,
				timestamp:         session.timestamp,
				closed_by_user:    session.closed_by_user === true
			};
		} catch {
			return null;
		}
	}

	/**
	 * Merges partial property updates into the local session storage entry.
	 * @param {Object} [updates={}] Partial session state to update.
	 * @returns {Object} Updated session object.
	 */
	saveSession(updates = {}) {
		const existing = this.getStoredSession() || {
			mode: 'ai',
			conversation_id: null,
			chatbot_session: null,
			transcript: [],
			pending_confirmation: null,
			closed_by_user: false
		};

		const merged = { ...existing, ...updates, timestamp: Date.now() };
		localStorage.setItem(ChatSupport.STORAGE_KEY, JSON.stringify(merged));
		return merged;
	}

	/**
	 * Updates local session timestamp to maintain storage validity.
	 */
	refreshSession() {
		this.saveSession({});
	}

	/**
	 * Marks session as manually closed to prevent automatically opening on page load.
	 */
	markSessionClosedByUser() {
		this.saveSession({ closed_by_user: true });
	}

	/**
	 * Clears manually closed status flag on the session.
	 */
	markSessionAsOpen() {
		this.saveSession({ closed_by_user: false });
	}

	/**
	 * Opens widget on page load if an unclosed session remains active.
	 */
	autoOpenIfNeeded() {
		const session = this.getStoredSession();
		if (!session || session.closed_by_user) return;

		if (session.mode === 'ai' && !session.transcript.length) return;

		if (!this.chat_settings_loaded) {
			this.chat_settings_loaded = true;
			this._loadChatSettings();
		}

		this._renderStaticTexts();
		this.widget.classList.add('open');
		document.body.classList.add('chat-support-open');
		this.resumeOrStartConversation();
	}

	/**
	 * Re-attaches interactive confirmation buttons to prompt bubbles after page reloads.
	 * @param {'clear'|'skip-escalation'|null} type Prompt type key.
	 */
	_restorePendingConfirmation(type) {
		const configs = {
			'clear': {
				class: 'clear-confirmation',
				buttons: [
					{ title: this.text_confirm_conversation_ending, action: 'confirm-clear-yes' },
					{ title: this.text_cancel_conversation_ending, action: 'confirm-clear-no' }
				]
			},
			'skip-escalation': {
				class: 'skip-escalation-confirmation',
				buttons: [
					{ title: this.text_skip_escalation_button_yes, action: 'confirm-skip-escalation-yes' },
					{ title: this.text_skip_escalation_button_no, action: 'confirm-skip-escalation-no' }
				]
			}
		};
		const config = configs[type];
		if (!config) return;

		if (type === 'clear' && !this.agent_joined) {
			this.saveSession({ pending_confirmation: null });
			return;
		}

		const last_message = this.history.querySelector('.message:last-child');
		const bubble = last_message?.querySelector('.bubble');
		if (!last_message || !bubble) return;

		last_message.classList.add(config.class);
		const buttons_html = this._buildQuickReplyButtonsHtml(config.buttons);
		bubble.insertAdjacentHTML('beforeend', `<div class="quick-replies">${buttons_html}</div>`);

		if (type === 'clear') this.awaiting_clear_confirmation = true;
		this.setInputState(false);
	}

	/**
	 * Fetches live agent chat log records and initializes polling for live session updates.
	 * @param {string} conversation_id Conversation ID.
	 * @param {number|null} [boundary_date=null] Timestamp cursor for fetching new logs.
	 */
	async initConversation(conversation_id, boundary_date = null) {
		try {
			this.conversation_id = conversation_id;

			const session = this.getStoredSession();
			this.last_update = boundary_date ?? (session?.last_chatbot_date ?? 0);

			const log = await this._post('live_refresh_log', { date: this.last_update });

			if (Array.isArray(log?.log)) {
				log.log.forEach(item => this._appendLogEntry(item));
			}

			if (log?.queue_position && !this.agent_joined) {
				this._showSearchingForAgentIndicator();
			}

			this._updateQueuePosition(log?.queue_position);
			this._updateFileUploadAvailability(log);
			this._scrollToBottom();

			this._handleSessionStatus(log?.status);
			if (this.polling_stopped) return;

			this.interaction.classList.remove('disabled');
			this.chatbot_authorized = true;
			this.stop_conversation_btn?.classList.remove('hidden');
			this._file_upload_is_live = true;
			this._refreshFileUploadVisibility();
			this._setKeepAliveVisible(true);

			this._restorePendingConfirmation(session?.pending_confirmation ?? null);

			this._startPolling();

			if (!_production) console.log('ChatSupport::initConversation conversation(%o) success', conversation_id);
		} catch (e) {
			console.error('ChatSupport::initConversation conversation(%o) failed', conversation_id, e);
		}
	}

	/**
	 * Triggers appropriate clearing workflow based on chat status (AI vs Live).
	 */
	async handleClearAction() {
		if (this.greeting_in_progress) return;

		const session = this.getStoredSession();
		const is_live = session?.mode === 'live';

		if (!is_live) {
			await this._resetServerSession();
			this.resetConversation();
			return;
		}

		this._showClearConfirmation();
	}

	/**
	 * Uploads a file to the live chat session, showing an optimistic
	 * "uploading" bubble while the request is in flight.
	 * @param {File} file Selected file
	 */
	async handleFileUploadAction(file) {
		const session = this.getStoredSession();
		const upload_id = this._showUploadingBubble(file.name);

		const form_data = new FormData();
		form_data.append('conversation_id', session?.conversation_id ?? '');
		form_data.append('upload', file);

		let success = false;
		try {
			const response = await this._post('live_file_upload', form_data);
			if (!_production) console.log('ChatSupport::handleFileUploadAction response(%o)', response);
			success = !!response?.result;
			this._resolveUploadingBubble(upload_id, success, file.name, file.type, response?.chat_log_id);
		} catch (e) {
			console.error('ChatSupport::handleFileUploadAction failed', e);
			this._resolveUploadingBubble(upload_id, false, file.name, file.type, null);
		}

		return success;
	}

	/**
	 * Renders an optimistic bubble showing a file upload in progress.
	 * @param {string} filename Original filename.
	 * @returns {string} Id used to locate this bubble in _resolveUploadingBubble().
	 */
	_showUploadingBubble(filename) {
		const upload_id = `upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

		this.history.insertAdjacentHTML('beforeend', `<div class="message x-body-md user file-uploading" data-upload-id="${upload_id}">
			<div class="message-row">
				<div class="bubble">
					<div class="file-upload-status">
						<span class="upload-spinner"></span>
						${this.escapeHtml(filename)}
					</div>
				</div>
			</div>
		</div>`);

		this._scrollToBottom();
		return upload_id;
	}

	/**
	 * Resolves a previously-rendered uploading bubble to its final state:
	 * - either shows a failure message or
	 * - replaces it with the uploaded file's thumbnail
	 * @param {string} upload_id Id returned by _showUploadingBubble().
	 * @param {boolean} success Whether the upload succeeded.
	 * @param {string} filename Original filename.
	 * @param {string} mime_type File's MIME type, used to decide whether it's clickable.
	 * @param {?number} chat_log_id Id returned by the server on success, needed to build the link.
	 */
	_resolveUploadingBubble(upload_id, success, filename, mime_type = '', chat_log_id = null) {
		const el = this.history.querySelector(`[data-upload-id="${upload_id}"]`);
		if (!el) return;

		el.removeAttribute('data-upload-id');
		el.classList.remove('file-uploading');

		if (!success) {
			el.classList.add('file-upload-failed');
			el.querySelector('.file-upload-status').innerHTML =
				`<span class="file-upload-failed">
					<span class="file-upload-failed-icon">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M12 19C8.13401 19 5 15.866 5 12C5 8.13401 8.13401 5 12 5C15.866 5 19 8.13401 19 12C19 15.866 15.866 19 12 19ZM12 20C16.4183 20 20 16.4183 20 12C20 7.58172 16.4183 4 12 4C7.58172 4 4 7.58172 4 12C4 16.4183 7.58172 20 12 20Z" fill="#F8541A"/>
							<path d="M9.25511 9.78615C9.24752 9.92237 9.3599 10.0327 9.49634 10.0327H10.3208C10.4589 10.0327 10.5687 9.92013 10.5872 9.78331C10.6762 9.12718 11.1265 8.64893 11.9292 8.64893C12.6148 8.64893 13.2432 8.9917 13.2432 9.81689C13.2432 10.4517 12.8687 10.7437 12.2783 11.188C11.6055 11.6768 11.0723 12.248 11.1104 13.1748L11.1133 13.3916C11.1152 13.5283 11.2266 13.6382 11.3633 13.6382H12.1743C12.3124 13.6382 12.4243 13.5263 12.4243 13.3882V13.2827C12.4243 12.5654 12.6973 12.356 13.4336 11.7974C14.043 11.334 14.6778 10.8198 14.6778 9.74072C14.6778 8.22998 13.4019 7.5 12.0054 7.5C10.7383 7.5 9.34964 8.09061 9.25511 9.78615ZM10.812 15.5488C10.812 16.082 11.2373 16.4756 11.8213 16.4756C12.4307 16.4756 12.8496 16.082 12.8496 15.5488C12.8496 14.9966 12.4307 14.6094 11.8213 14.6094C11.2373 14.6094 10.812 14.9966 10.812 15.5488Z" fill="#F8541A"/>
						</svg>
					</span>
					${this.text_upload_failure}<br />
					${this.escapeHtml(filename)}
				</span>`;
			this._scrollToBottom();
			return;
		}

		const is_image = mime_type.indexOf('image/') === 0;
		const status_el = el.querySelector('.file-upload-status');

		if (is_image && chat_log_id) {
			const thumbnail_url = this._attachmentUrl(chat_log_id);

			status_el.outerHTML = `<a href="#" class="attachment_link attachment-thumbnail" data-chat-log-id="${chat_log_id}"><img src="${thumbnail_url}" alt="${this.escapeHtml(filename)}" loading="lazy" /><span class="attachment-thumbnail-filename">${this.escapeHtml(filename)}</span></a>`;
			this.pending_upload_count = (this.pending_upload_count || 0) + 1;

			const img = el.querySelector('.attachment-thumbnail img');
			if (img && !img.complete) {
				img.addEventListener('load', () => this._scrollToBottom(), { once: true });
			}

			this._scrollToBottom();
			return;
		}

		const filename_html = is_image && chat_log_id
			? `<a href="#" class="attachment_link" data-chat-log-id="${chat_log_id}">${this.escapeHtml(filename)}</a>`
			: this.escapeHtml(filename);

		status_el.outerHTML =
			`<span class="file-uploaded"><svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 24 24" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M24 19H21V23H19V19H16L20 15L24 19ZM21.0078 3C21.5555 3 21.9999 3.44482 22 3.99316V13H20V5H4V18.999L14 9L17 12V14.8291L14 11.8281L6.82715 19H14V21H2.99219C2.44451 21 2.00013 20.5552 2 20.0068V3.99316C2.00013 3.44463 2.45577 3 2.99219 3H21.0078ZM8 7C9.10457 7 10 7.89543 10 9C10 10.1046 9.10457 11 8 11C6.89543 11 6 10.1046 6 9C6 7.89543 6.89543 7 8 7Z"></path></svg> ${filename_html}</span>`;

		this.pending_upload_count = (this.pending_upload_count || 0) + 1;
		this._scrollToBottom();
	}

	/**
	 * Builds the URL that serves an uploaded attachment of the current conversation.
	 * @param {number|string} chat_log_id Id of the chat log entry holding the attachment.
	 * @returns {string} Attachment URL.
	 */
	_attachmentUrl(chat_log_id) {
		const params = new URLSearchParams({
			action: 'live_get_attachment',
			chat_log_id
		});

		return `${url(ChatSupport.ENDPOINT)}?${params}`;
	}

	/**
	 * Opens the attachment preview modal for a clicked attachment link
	 * @param {HTMLElement} link The clicked attachment link element
	 */
	openAttachmentModal(link) {
		const chat_log_id = link.dataset.chatLogId;
		const filename = link.textContent;
		if (!chat_log_id) return;

		const attachment_url = this._attachmentUrl(chat_log_id);

		const modal = this._ensureAttachmentModal();
		const modal_content = modal.querySelector('.attachment-modal-content');

		modal.querySelector('.attachment-modal-filename').textContent = filename;
		modal_content.innerHTML = '';

		const img = document.createElement('img');
		img.src = attachment_url;
		img.alt = filename;
		modal_content.appendChild(img);

		modal.classList.add('open');
	}

	/**
	 * Builds (once) and caches the attachment preview modal
	 */
	_ensureAttachmentModal() {
		if (this.attachment_modal) return this.attachment_modal;

		const modal = document.createElement('div');
		modal.className = 'chat-attachment-modal';
		modal.innerHTML = `
			<div class="attachment-modal-inner">
				<div class="attachment-modal-header">
					<span class="attachment-modal-filename"></span>
					<button type="button" class="attachment-modal-close" aria-label="Close">&times;</button>
				</div>
				<div class="attachment-modal-content"></div>
			</div>
		`;

		const close = () => {
			modal.classList.remove('open');
			modal.querySelector('.attachment-modal-content').innerHTML = '';
		};

		modal.querySelector('.attachment-modal-close').addEventListener('click', close);
		modal.addEventListener('click', e => {
			if (e.target === modal) close();
		});

		document.addEventListener('keydown', e => {
			if (e.key === 'Escape' && modal.classList.contains('open')) close();
		});

		document.body.appendChild(modal);
		this.attachment_modal = modal;
		return modal;
	}

	/**
	 * Displays confirmation prompt asking if user wishes to end current session.
	 */
	_showClearConfirmation() {
		if (this.history.querySelector('.clear-confirmation')) return;

		this._appendBubble(
			'bot event clear-confirmation',
			this.text_confirm_end_conversation,
			[
				{ title: this.text_confirm_conversation_ending, action: 'confirm-clear-yes' },
				{ title: this.text_cancel_conversation_ending, action: 'confirm-clear-no' }
			]
		);
		this._logEvent('text_agent', this.text_confirm_end_conversation);

		this.saveSession({ pending_confirmation: 'clear' });

		this.awaiting_clear_confirmation = true;
		this.setInputState(false);
	}

	/**
	 * Displays inactivity warning message if threshold is reached.
	 */
	_checkTimeoutWarning() {
		if (!this.agent_joined || !this.last_activity_date || this.timeout_warning_shown) return;
		if (!this.timeout_interval || !this.countdown_interval) return;

		const warn_after = this.timeout_interval - this.countdown_interval;
		const elapsed = Math.floor(Date.now() / 1000) - this.last_activity_date;

		if (elapsed >= warn_after) {
			this.timeout_warning_shown = true;
			const notice = `${this.text_chat_timeout_warning.replace('{time}', `${this.countdown_interval} seconds`)}`;
			this._appendBubble('bot event', notice);
		}
	}

	/**
	 * Converts integer values to formatted ordinal strings (e.g. 1st, 2nd, 3rd).
	 * @param {number|string} value Input number.
	 * @returns {string} Ordinal string representation.
	 */
	_formatOrdinal(value) {
		const num = Number(value);
		if (!Number.isFinite(num)) return String(value);

		const int = Math.trunc(num);
		const abs = Math.abs(int);
		const last_two = abs % 100;

		let suffix = 'th';
		if (last_two < 11 || last_two > 13) {
			switch (abs % 10) {
				case 1: suffix = 'st'; break;
				case 2: suffix = 'nd'; break;
				case 3: suffix = 'rd'; break;
			}
		}

		return `${int}${suffix}`;
	}

	/**
	 * Updates queue location element text inside active searching indicator.
	 * @param {number|null} position Position index in agent queue.
	 */
	_updateQueuePosition(position) {
		const el = this.history.querySelector('.searching-indicator .queue-position');
		if (!el) return;
		el.textContent = position ? this.text_queue_position.replace('{position}', this._formatOrdinal(position)) : '';
	}

	/**
	 * Evaluates server status response and ends session if marked as failed or expired.
	 * @param {string|null} status Server status string.
	 */
	_handleSessionStatus(status) {
		if (this.polling_stopped) return;

		if (status === 'expired') {
			this._endConversation();
			this._showBotEndedPrompt();
			return;
		}

		if (status !== 'failed') return;
		const had_agent = this.agent_joined;
		this._endConversation();
		this._showConversationEndedPrompt(had_agent, true);
	}

	/**
	 * Toggles the upload panel based on server-reported availability
	 * @param {Object} response Response payload from a live_refresh_log call.
	 */
	_updateFileUploadAvailability(response) {
		const upload_enabled = !!response?.upload_enabled;

		if (this._upload_disable_pending) {
			// a poll that started before our disable landed would re-open the uploader
			if (upload_enabled) return;
			this._upload_disable_pending = false;
		}

		// the agent re-enabled uploads: start a fresh cycle
		if (upload_enabled && this._upload_server_enabled === false) {
			this._upload_cycle = { intended: 0, succeeded: 0 };
		}
		this._upload_server_enabled = upload_enabled;

		const max_upload_file_size = Math.max(0, Math.floor(Number(response?.max_upload_file_size)) || 0);
		if (max_upload_file_size !== this.max_upload_file_size) {
			this.max_upload_file_size = max_upload_file_size;
			this._renderUploadConditions();
		}

		const uploads_remaining = Math.max(0, Math.floor(Number(response?.uploads_remaining)) || 0);
		this._file_upload_server_available = upload_enabled && uploads_remaining > 0;
		this._setMaxUploadFiles(uploads_remaining);
		this._refreshFileUploadVisibility();
	}

	/**
	 * Applies the server-reported upload limit: resizes the pick slots to match and
	 * drops any selected files that no longer fit within the limit.
	 * @param {number} count Number of files the user is still allowed to upload.
	 */
	_setMaxUploadFiles(count) {
		if (count === this.max_upload_files) return;
		this.max_upload_files = count;
		this._renderUploadConditions();

		if (!this.upload_buttons_container) return;

		this.selected_upload_files.slice(count).forEach(f => this._removeSelectedUploadFile(f.wrapper));
		this._ensureUploadWrapperCount(Math.max(count, 1));
		this._refreshUploadControlsVisibility();
	}

	/**
	 * Recomputes file-upload visibility from the combined is_live + server-available state
	 */
	_refreshFileUploadVisibility() {
		if (!this.file_upload_holder) return;

		const visible = this._file_upload_is_live && this._file_upload_server_available;
		const was_visible = !this.file_upload_holder.classList.contains('hidden');

		this.file_upload_holder.classList.toggle('hidden', !visible);

		// only re-anchor when the panel toggles (it resizes the history area), not on every poll
		if (visible !== was_visible) this._scrollToBottom();
	}

	/**
	 * Prompts user to confirm skipping queue waiting time for live support.
	 * @param {HTMLElement} button Button trigger element.
	 */
	_showSkipEscalationConfirmation(button) {
		button.disabled = true;

		this._appendBubble(
			'bot event skip-escalation-confirmation',
			this.text_skip_escalation_confirmation,
			[
				{ title: this.text_skip_escalation_button_yes, action: 'confirm-skip-escalation-yes' },
				{ title: this.text_skip_escalation_button_no, action: 'confirm-skip-escalation-no' }
			]
		);
		this._logEvent('text_agent', this.text_skip_escalation_confirmation);
		this.saveSession({ pending_confirmation: 'skip-escalation' });
		this.setInputState(false);
	}

	/**
	 * Processes response to skipping live agent waiting queue.
	 * @param {boolean} confirmed Decision flag.
	 */
	async _onSkipEscalationConfirmation(confirmed) {
		this._appendBubble('user', confirmed ? this.text_skip_escalation_button_yes : this.text_skip_escalation_button_no);
		this.saveSession({ pending_confirmation: null });

		if (!confirmed) {
			const searching = this.history.querySelector('.searching-indicator');
			if (searching) {
				const no_wait_btn = searching.querySelector('button[data-action="no-waiting-for-agent"]');
				if (no_wait_btn) no_wait_btn.disabled = false;

				const clone = searching.cloneNode(true);
				searching.remove();

				this.history.insertAdjacentElement('beforeend', clone);
			}

			this._scrollToBottom();
			return;
		}

		this.awaiting_clear_confirmation = false; 
		this.history.querySelector('.message.skip-escalation-confirmation')?.remove();
		try {
			await this._post('live_skip_waiting_for_agent');
		} catch (e) {
			console.error('ChatSupport::live_skip_waiting_for_agent failed', e);
		}

		this._removeSearchingIndicator();
		this._appendBubble(
			'bot event',
			this.text_escalation_trough_email, [{
				title: this.text_button_restart_conversation,
				action: 'ticket-ended-restart'
			}]
		);
		this._endConversation();
	}

	/**
	 * Handles response to clear conversation confirm dialog
	 * @param {boolean} confirmed True if clearing session confirmed
	 */
	async _onClearConfirmation(confirmed) {
		this.awaiting_clear_confirmation = false;
		this.history.querySelector('.message.clear-confirmation')?.classList.remove('clear-confirmation');

		this._logEvent('text_customer', confirmed ? this.text_confirm_conversation_ending : this.text_cancel_conversation_ending);
		this._scrollToBottom();
		const had_agent = this.agent_joined;

		if (!confirmed) {
			this.saveSession({ pending_confirmation: null });
			this.setInputState(true);
			return;
		}

		this._endConversation();

		// keeps the chat server-side for the transcript email
		try {
			await this._post('live_end_conversation');
		} catch (e) {
			console.error('ChatSupport::live_end_conversation failed', e);
		}
		this._showConversationEndedPrompt(had_agent);
	}

	/**
	 * Sends backend request to reset and destroy conversation session state.
	 */
	async _resetServerSession() {
		try {
			await this._post('reset_conversation');
		} catch (e) {
			console.error('ChatSupport::_resetServerSession failed', e);
		}
	}

	/**
	 * Resets local instance state, history DOM, and storage to initial conditions.
	 */
	resetConversation() {
		this._resetUploadSelection();
		clearTimeout(this.poll_timer);
		this.poll_timer = null;
		this.polling_stopped = false;
		this.polling_in_flight = false;
		this.last_update = 0;
		this.pending_sent_count = 0;
		this.pending_upload_count = 0;
		this.awaiting_quick_reply = false;
		this.awaiting_clear_confirmation = false;

		this.chatbot_session = null;
		this.conversation_id = null;
		this.last_chatbot_date = 0;
		this.chatbot_authorized = false;
		this.chatbot_authorize_promise = null;
		this.transcript = [];
		this.agent_joined = false;
		this.agent_notice_shown = false;
		this.escalated = false;
		this.stop_conversation_btn?.classList.add('hidden');
		this._file_upload_is_live = false;
		this._resetUploadCycle();
		this._refreshFileUploadVisibility();
		this._setKeepAliveVisible(false);

		this._resetHistoryDom();

		localStorage.removeItem(ChatSupport.STORAGE_KEY);

		this.interaction.classList.remove('disabled');

		this.textarea.value = '';
		this.textarea.style.height = ChatSupport.MIN_TEXTAREA_HEIGHT + 'px';
		this.setInputState(true);
		this._updateToolbarButtons();
	}

	/**
	 * Clears server/local data and prepares UI state for a new conversation.
	 */
	async _startNewConversation() {
		this.resetConversation();
		await this._resetServerSession();
		this.textarea.focus();
	}

	/**
	 * Asynchronously fetches configuration timeouts for chat inactivity warnings.
	 */
	async _loadChatSettings() {
		try {
			const response = await this._post('get_chat_settings');

			if (!_production) console.log('ChatSupport::_loadChatSettings response(%o)', response);
			this.timeout_interval = Number(response?.timeout_interval) || 0;
			this.countdown_interval = Number(response?.countdown_interval) || 0;
		} catch (e) {
			console.error('ChatSupport::_loadChatSettings failed', e);
		}
	}

	/**
	 * Asynchronously logs a UI event to backend activity records.
	 * @param {'text_agent'|'text_customer'} type Message author type.
	 * @param {string} message Logged payload string.
	 */
	async _logEvent(type, message) {
		try {
			const response = await this._post('live_log_event', {
				type: type === 'text_chatbot' ? 'text_agent' : type,
				message
			});
			if (response?.server_time) {
				this.last_update = Math.max(this.last_update, response.server_time);
				this.last_activity_date = Math.max(this.last_activity_date, response.server_time);
				this.timeout_warning_shown = false;
			}
		} catch (e) {
			console.error('ChatSupport::live_log_event failed', e);
		}
	}

	/**
	 * Toggles visibility of toolbar stop/clear buttons according to active mode.
	 */
	_updateToolbarButtons() {
		const is_live = this.getStoredSession()?.mode === 'live';
		this.stop_conversation_btn?.classList.toggle('hidden', !is_live);
		this._file_upload_is_live = is_live;
		this._refreshFileUploadVisibility();
		this.clear_conversation_btn?.classList.toggle('hidden', is_live);
		this._setKeepAliveVisible(is_live);
	}

	/**
	 * Toggles keep-alive checkbox for the current live/non-live state
	 * @param {boolean} visible True when a live conversation is active.
	 */
	_setKeepAliveVisible(visible) {
		this.keep_alive_wrapper?.classList.toggle('hidden', !visible);

		if (visible && this._keepalive_enabled) {
			this._startKeepAlive();
		} else {
			this._stopKeepAliveTimer();
		}
	}

	/**
	 * Starts sending periodic keep-alive log events to keep the live session active.
	 */
	_startKeepAlive() {
		if (this._keepalive_interval) return;

		this._sendKeepAlive();
		this._keepalive_interval = setInterval(() => this._sendKeepAlive(), ChatSupport.KEEPALIVE_INTERVAL_MS);
	}

	/**
	 * Stops the keep-alive interval, without touching the persisted enabled preference.
	 */
	_stopKeepAliveTimer() {
		clearInterval(this._keepalive_interval);
		this._keepalive_interval = null;
	}

	/**
	 * Sends a keep-alive message through the normal live chat pipeline.
	 */
	_sendKeepAlive() {
		this.sendMessage('keep-alive');
	}
}
